/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  LastViewedPosition,
  LiveWatchHistoryItem
} from "@tivocorp/data-domain-lib/client-code/watch-history";
import { WatchHistoryService } from "@tivocorp/data-domain-lib";

import type { IWatchHistoryInteractor } from "../../interfaces";
import type { TNetworkOptions } from "../../types";
import { Log } from "../../logging";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";

/**
 * Facade for Watch History operations (last-viewed position + recently-watched).
 * Resolves the `watch-history-service` endpoint via TSRI, builds authenticated
 * params and runs every call through the NetworkGuard layer.
 *
 * All operations return `204 No Content`, so each resolves to `void`.
 */
export class WatchHistoryInteractor
  extends BaseInteractor
  implements IWatchHistoryInteractor
{
  private static readonly TAG: string = "WatchHistoryInteractor";

  private async buildService(): Promise<WatchHistoryService> {
    const params = await this.buildServicesParams(
      TIVO_SERVICES.WatchHistoryService,
      ApplicationFeatureArea.ALL
    );
    return new WatchHistoryService(params);
  }

  public async saveVodLastViewedPosition(
    lastViewedPosition: LastViewedPosition,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "saveVodLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(WatchHistoryInteractor.TAG, "saveVodLastViewedPosition() called");
        const service: WatchHistoryService = await this.buildService();
        return service.saveVodLastViewedPosition(
          { lastViewedPosition },
          guardedSignal
        );
      },
      options
    );
  }

  public async saveSocuLastViewedPosition(
    lastViewedPosition: LastViewedPosition,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "saveSocuLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(
          WatchHistoryInteractor.TAG,
          "saveSocuLastViewedPosition() called"
        );
        const service: WatchHistoryService = await this.buildService();
        return service.saveSocuLastViewedPosition(
          { lastViewedPosition },
          guardedSignal
        );
      },
      options
    );
  }

  public async saveLiveRecentlyWatched(
    liveWatchHistoryItem: LiveWatchHistoryItem,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "saveLiveRecentlyWatched",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(WatchHistoryInteractor.TAG, "saveLiveRecentlyWatched() called");
        const service: WatchHistoryService = await this.buildService();
        return service.saveLiveRecentlyWatched(
          { liveWatchHistoryItem },
          guardedSignal
        );
      },
      options
    );
  }

  public async deleteVodRecentlyWatched(
    offerId: string,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "deleteVodRecentlyWatched",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(WatchHistoryInteractor.TAG, "deleteVodRecentlyWatched() called");
        const service: WatchHistoryService = await this.buildService();
        return service.deleteVodRecentlyWatched({ offerId }, guardedSignal);
      },
      options
    );
  }

  public async deleteSocuRecentlyWatched(
    offerId: string,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "deleteSocuRecentlyWatched",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(WatchHistoryInteractor.TAG, "deleteSocuRecentlyWatched() called");
        const service: WatchHistoryService = await this.buildService();
        return service.deleteSocuRecentlyWatched({ offerId }, guardedSignal);
      },
      options
    );
  }

  public async deleteLiveRecentlyWatched(
    channelId: string,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "deleteLiveRecentlyWatched",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(WatchHistoryInteractor.TAG, "deleteLiveRecentlyWatched() called");
        const service: WatchHistoryService = await this.buildService();
        return service.deleteLiveRecentlyWatched({ channelId }, guardedSignal);
      },
      options
    );
  }
}
